import { Injectable } from '@angular/core';


import { BehaviorSubject, forkJoin, Observable,
    of, Subscription, switchMap, iif, merge, combineLatest } from 'rxjs';
import { filter, map, tap, shareReplay, take, catchError } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { MediaSubscriptionApiService } from '../../../api/mediaSubscriptions/media-subscription.api.service';
import { MediaSubscriptionApi } from '../../../api/mediaSubscriptions/models/media-subscription.api.model';
import { QuotaInformation } from '../../../api/mediaSubscriptions/models/quota-information.api.model';
import { MonitoringQuotaTypesEnum } from '../../../api/mentions/enum/monitoring-quota-types.enum';
import { QuotaHistoryInformation } from '../../../api/mediaSubscriptions/models/quota-history-information.api.model';
import { SubscriptionContentTimeslot } from '../../../api/mediaSubscriptions/models/subscription-content-timeslot.api.model';
import { ConfirmDialogComponent } from '../../../Common/Dialogs/components/ConfirmDialog/confirm-dialog.component';
import { ConfirmDialogModel } from '../../../Common/Dialogs/models/confirm-dialog.model';
import { FilterListProviderServiceInterface } from '../../../Common/Filter/services/filter-list-provider-service.interface';
import { DateQueryModel } from '../../../Common/Forms/models/date-query.model';
import { messageSeverityEnum } from '../../../Common/Misc/enums/message-severity.enum';
import { AuditableModel } from '../../../Common/Misc/models/auditable.model';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { userPermissionsEnum } from '../../../Core/Users/enums/user-permissions.enum';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { MediaSubscription } from '../../Shared/models/media-subscription.model';
import { MentionSearchCriteriaModel } from '../../Shared/models/mention-search-criteria.model';
import { WebsocketAwareNeptuneService, ResourceType } from '../../../Bootstrap/services/websocket-aware-neptune.service';

import { MentionSearchModel } from '../../Shared/models/mention-search.model';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { MentionSearchService } from './mention-search.service';
import { MonitoringRoutingService } from './monitoring-routing.service';
import { SearchResultsService } from './search-results.service';
import { ActionsEnum } from '../../../Common/Basic/enums/actions.enum';
import { NewResourceDialogModel } from '../../../Common/Dialogs/models/new-resource-dialog.model';
import { NewResourceDialogComponent } from '../../../Common/Dialogs/components/NewResourceDialog/new-resource-dialog.component';
import { DisplayGroupModel } from '../../../Common/Layout/models/display-group.model';
import { DisplayGroupsService } from '@Common/Layout/services/display-groups.service';
import { CurrentResourceAbstractService } from '@Common/Basic/service/current-resource-abstract.service';
import { MultiBaseAbstractApi } from '@api/genericApi/multi-base-abstract.api';
import { ResourceAction } from '@Common/Basic/service/resource-abstract.service';
import { ResourceActionTypes } from '@Common/Basic/enums/resource-actions.enum';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';
import { CommonApiErrorDetailsModel } from '@api/genericApi/models/common-api-error-details.model';

@Injectable()
export class MediaSubscriptionService extends CurrentResourceAbstractService<MediaSubscription, MediaSubscriptionApi, MediaSubscription, MediaSubscriptionApi>
    implements FilterListProviderServiceInterface {

    all$: Observable<MediaSubscription[]> = this.userStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions]).pipe(
        switchMap((hasPermission) =>
            hasPermission ? merge(
                this.getApiService().getList().pipe(
                    map(list => list.map(item => this.constructListModel(item)))
                ),
                this.refreshAllManualSubject
            ) : of([])
        ),
        map((subs: MediaSubscription[]) => subs.filter(sub => !sub.isAgent)),
        shareReplay(1)
    );

    currentResource$: Observable<MediaSubscription> = this.currentResourceSubject.asObservable().pipe(
        tap(resource => {
            if (resource?.id) {
                this.cachedResource = resource.clone();
            } else {
                this.cachedResource = null;
            }
        }),
        shareReplay(1)
    );

    private currentRoute: MonitoringRouteModel;

    private mediaSubscriptionQuotaInfo$: BehaviorSubject<QuotaInformation | null>;
    private mediaSubscriptionQuotaExceeded$: BehaviorSubject<boolean | null>;
    private socialListeningQuotaInfo$: BehaviorSubject<QuotaInformation | null>;
    private socialListeningQuotaExceeded$: BehaviorSubject<boolean | null>;
    protected subscriptions: Subscription[] = [];

    readonly DUPLICATE_NAME_ERROR_CODE = '4001005';

    constructor(
        private mediaSubscriptionApiService: MediaSubscriptionApiService,
        private messageService: MessageService,
        private monitoringRoutingService: MonitoringRoutingService,
        private mentionSearchService: MentionSearchService,
        private userStateService: CurrentUserStateService,
        private searchResultsService: SearchResultsService,
        private websocketEvents: WebsocketAwareNeptuneService,
        private displayGroupsService: DisplayGroupsService,
        public localizationService: LocalizationService,
        public coreDialogService: CoreDialogService,
    ) {
        super();

        this.mediaSubscriptionQuotaInfo$ = new BehaviorSubject<QuotaInformation | null>(null);
        this.mediaSubscriptionQuotaExceeded$ = new BehaviorSubject<boolean | null>(null);
        this.socialListeningQuotaInfo$ = new BehaviorSubject<QuotaInformation | null>(null);
        this.socialListeningQuotaExceeded$ = new BehaviorSubject<boolean | null>(null);

        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().pipe(
                filter(route => !!route),
                switchMap((route) => {
                    return this.onUpdateRoute(route);
                })
            ).subscribe(),
            this.userStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions, userPermissionsEnum.socialIntelligence]).pipe(
                filter(hasPermission => !!hasPermission),
                switchMap(() => this.mediaSubscriptionApiService.getAccountQuotaInfo())
            ).subscribe(quotaInfoList => {
                const mediaSubscriptionQuotaInfo = quotaInfoList?.find(info => info.quotaType === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION) ?? null;
                const socialListeningQuotaInfo = quotaInfoList?.find(info => info.quotaType === MonitoringQuotaTypesEnum.SOCIAL_LISTENING) ?? null;

                this.mediaSubscriptionQuotaInfo$.next(mediaSubscriptionQuotaInfo);
                this.socialListeningQuotaInfo$.next(socialListeningQuotaInfo);

                this.mediaSubscriptionQuotaExceeded$.next(this.isQuotaExceeded(mediaSubscriptionQuotaInfo));
                this.socialListeningQuotaExceeded$.next(this.isQuotaExceeded(socialListeningQuotaInfo));
            }),
            this.websocketEvents.getWebsocketMessages().pipe(
                filter(x => !!x),
                switchMap(message => {
                    if (message.resourceType === ResourceType.socialSubscriptions) {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                        );
                    }
                    return of(null);
                })
            ).subscribe()
        );
    }

    getApiService(): MultiBaseAbstractApi<MediaSubscriptionApi, MediaSubscriptionApi> {
        return this.mediaSubscriptionApiService;
    }

    private isQuotaExceeded(quotaInfo: QuotaInformation | null): boolean | null {
        if (!quotaInfo) {
            return null;
        }
        const totalQuota = (quotaInfo.maxCount ?? 0) + (quotaInfo.additionalCount ?? 0);
        const totalQuotaUsed = quotaInfo.currentCount ?? 0;
        return totalQuotaUsed > totalQuota;
    }

    constructListModel(apiModel: MediaSubscriptionApi): MediaSubscription {
        return new MediaSubscription(apiModel);
    }

    constructResourceModel(apiModel: MediaSubscriptionApi): MediaSubscription {
        return new MediaSubscription(apiModel);
    }


    // @TODO updating the group like this is overkill, update on MediaSubscription instead
    create(subscription: MediaSubscription, group?: DisplayGroupModel): Observable<number> {
        return this.createResource(subscription, {
            actionType: ResourceActionTypes.CREATE,
            refreshResource: true,
        }).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
            switchMap((id) => {
                if (group && id) {
                    return this.updateGroup([id], group).pipe(
                        map(x => id)
                    );
                } else {
                    return of(id);
                }
            }),
        );
    }


    update(subscription: MediaSubscription, actionType: ResourceActionTypes): Observable<number> {
        return this.updateResource(subscription, { actionType: actionType, refreshResource: true }).pipe(
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            }),
        );
    }


    /**
     * Delete the specified subscription and force refresh of all$ before returning id.
     * If the currentResource is the subscription being deleted, nav to hype screen & reset currentResource to null.
     * @param subscription - Media Subscription that is being deleted
     */
    delete(subscription: MediaSubscription): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedSub) => {
                return this.deleteResource(subscription.id, {
                    actionType: ResourceActionTypes.DELETE,
                    resetResource: focusedSub?.id === subscription.id,
                    resource: subscription
                }).pipe(
                    tap(() => {
                        if (focusedSub?.id === subscription.id) {
                            this.monitoringRoutingService.gotoSubscriptionHype();
                        }
                    })
                );
            }),
            switchMap((subId) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => subId)
                );
            }),
        );
    }

    /**
     * Adds provided subscription to group (or removes group ID if Uncategorized is selected)
     * @param subscriptionIds - Media Subscription Ids
     * @param group undefined if Uncategorized is selected
     * @param refreshAll when true triggers a fresh of all$
     */
    updateGroup(subscriptionIds: number[], group: DisplayGroupModel, refreshAll: boolean = false): Observable<MediaSubscription> {
        let request: Observable<void>;

        if (group.id) {
            request = this.mediaSubscriptionApiService.updateDisplayGroup(subscriptionIds, [], group.id);
        } else {
            request = this.mediaSubscriptionApiService.removeDisplayGroup(subscriptionIds);
        }

        return request.pipe(
            catchError(() => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                );
            }),
            switchMap(() =>
                iif(
                    () => refreshAll,
                    this.refreshAllManual().pipe(
                        filter(x => !!x),
                        take(1),
                    ),
                    of(null)
                )
            ),
            switchMap(() =>
                iif(
                    () => subscriptionIds.length === 1,
                    this.getById(subscriptionIds[0]),
                    of(null)
                )
            ),
            tap((subscription: MediaSubscription) => {
                // @TODO this move messages are "moved to" but it could be removed
                if (subscription) {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.single',
                            { itemName: subscription?.name, groupName: group.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Common.lhp.moveToGroup.success.summary',
                        detail: this.localizationService.transform('Common.lhp.moveToGroup.success.detail.multiple',
                            { number: subscriptionIds.length, name: group.name }
                        )
                    });
                }
            })
        );
    }

    activateCurrentSubscription(): Observable<number> {
        return combineLatest([
            this.currentResource$.pipe(take(1)),
            this.mediaSubscriptionApiService.getAccountQuotaInfo(),
        ]).pipe(
            switchMap(([focusedSubscription, quotaInfoList]) => {
                const quotaInfo = quotaInfoList?.find(info => info.quotaType === MonitoringQuotaTypesEnum.MEDIA_SUBSCRIPTION) ?? quotaInfoList?.[0];
                const quotaExceeded = !!quotaInfo && (quotaInfo.currentCount ?? 0) > ((quotaInfo.maxCount ?? 0) + (quotaInfo.additionalCount ?? 0));

                if (focusedSubscription.isModeratedByQuota() && quotaExceeded) {
                    focusedSubscription.status = MediaSubscriptionStatusEnum.STAND_BY;
                } else {
                    focusedSubscription.status = MediaSubscriptionStatusEnum.ACTIVE;
                }

                return this.update(focusedSubscription, ResourceActionTypes.ACTIVATED);
            })
        );
    }

    pauseCurrentSubscription(): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap(focusedSubscription => {
                focusedSubscription.status = MediaSubscriptionStatusEnum.PAUSED;

                return this.update(focusedSubscription, ResourceActionTypes.PAUSED);
            })
        );
    }

    /**
     * Check for duplicates, then rename the specified targeting list
     * @TODO see NEP-32699 for validation improvements
    */
    renameListDupCheck(id: number, name: string): Observable<number> {
        return this.isNameDuplicated$(name, id).pipe(
            switchMap((duplicate) => {
                if (duplicate) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MediaSubscriptions.createSubscriptionErrorHeading',
                        detail: 'MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'
                    });
                    throw new Error(CommonApiErrorEnum.duplicateName);
                } else {
                    return this.rename(id, name);
                }
            })
        );
    }

    /**
     * Rename the specified subscription (via id) with the new name and force refresh of all$ before returning subId.
     * If the currentResource is the list being renamed, force a refresh of the currentResource.
     * @param subId - MediaSubscription id that is being renamed
     * @param name - the new updated name property
     */
    rename(subId: number, name: string): Observable<number> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((currentSubscription) => {
                return this.getById(subId).pipe(
                    switchMap((subscription) => {
                        subscription.name = name;
                        return this.updateResource(subscription, {
                            actionType: ResourceActionTypes.RENAME,
                            refreshResource: currentSubscription?.id === subId,
                        });
                    }),
                    switchMap((id) => {
                        return this.refreshAllManual().pipe(
                            filter(x => !!x),
                            take(1),
                            map(x => id)
                        );
                    })
                );
            })
        );
    }

    /**
     * Update the hidden state of the specified subcription (via id) and force refresh of all$ before returning id.
     */
    updateVisibility(subId: number, action: ActionsEnum): Observable<number> {
        return this.getById(subId).pipe(
            switchMap(sub => {
                sub.hidden = action === ActionsEnum.hide;
                return this.updateResource(sub, { actionType: ResourceActionTypes.UPDATE }, false);
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /**
     * Similar logic is used across different services.
     *
     * @TODO Consider consolidating repeated bulk deletion logic using shared CRUD interfaces.
     */
    confirmBulkDelete(items: AuditableModel[], groups: DisplayGroupModel[]): Observable<boolean> {
        const list = items.map(item => item.name);
        const secondaryList = groups.map(item => item.name);

        return new Observable<boolean>((subscriber) => {
            let isListSelected: boolean = true;
            let isSecondaryListSelected: boolean = false;

            const io: ConfirmDialogModel = {
                inputs: {
                    id: 'MediaSubscriptionsBulkDelete',
                    heading: 'Monitoring.bulkDeleteSubscription',
                    alertType: messageSeverityEnum.warning,
                    alertMessage: 'Monitoring.confirmBulkDeleteSubscription.alertMessage',
                    message: 'Common.confirmDialog.message',
                    list,
                    listLabel: 'Monitoring.confirmBulkDeleteSubscription.checkboxLabel',
                    isListSelected,
                    secondaryList,
                    secondaryListLabel: 'Common.confirmDialog.checkboxLabel.groups',
                    isSecondaryListSelected,
                    okLabel: 'Core.button.delete'
                },
                outputs: {
                    listSelectionChanged: (isSelected: boolean) => {
                        isListSelected = isSelected;
                    },
                    secondaryListSelectionChanged: (isSelected: boolean) => {
                        isSecondaryListSelected = isSelected;
                    },
                    dialogClose: (confirmed: any) => {
                        if (confirmed) {
                            if (isListSelected) {
                                const ids = items.map(item => item.id);
                                this.bulkDelete(ids).subscribe();
                            }

                            if (isSecondaryListSelected) {
                                groups.forEach(group => this.displayGroupsService.deleteGroup(group));
                            }
                        }
                        subscriber.next(confirmed);
                        subscriber.complete();
                    }
                }
            };
            this.coreDialogService.open(ConfirmDialogComponent, io);
        });
    }

    bulkDelete(ids: number[]): Observable<number[]> {
        return this.currentResource$.pipe(
            take(1),
            switchMap((focusedResource) => {
                const action = {
                    actionType: ResourceActionTypes.BULK_DELETE,
                    resetResource: ids.includes(focusedResource?.id),
                    count: ids.length
                };

                return this.bulkDeleteResourceByIds(ids, action).pipe(
                    tap(() => {
                        if (ids.includes(focusedResource?.id)) {
                            this.monitoringRoutingService.gotoSubscriptionHype();
                        }
                    })
                );
            }),
            switchMap((id) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => id)
                );
            })
        );
    }

    /**
     * Update the hidden state of the specified subscriptions and force refresh of all$ before returning subscription ids.
     */
    bulkUpdateVisibility(ids: number[], action: ActionsEnum): Observable<number[]> {
        return forkJoin(ids.map(id => this.getById(id))).pipe(
            switchMap((subscriptions: MediaSubscription[]) => {
                subscriptions.forEach(sub => sub.hidden = action === ActionsEnum.hide);
                return this.updateBulkResources(subscriptions);
            }),
            switchMap((subIds) => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => subIds)
                );
            })
        );
    }

    openCopySubscriptionDialog(subscription: MediaSubscription) {
        const io: NewResourceDialogModel = {
            inputs: {
                heading: 'Monitoring.copySubscription',
                initialValue: subscription.name + this.localizationService.transform('Common.dialog.newResource.copy.suffix'),
                currentGroup: subscription.groupId,
                okLabel: 'Common.dialog.newResource.copy',
                observableFormValidator: (name: string) => {
                    return this.isNameDuplicated$(name).pipe(
                        map((isDuplicate) => {
                            if (isDuplicate) {
                                return ['MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'];
                            } else {
                                return [];
                            }
                        })
                    );
                },
                submit: (name: string, group?: DisplayGroupModel) => {
                    return this.getById(subscription.id).pipe(
                        switchMap(_subscription => {
                            const newSubscription = _subscription.cloneForCopy();
                            newSubscription.name = name;
                            newSubscription.groupId = group?.id;
                            newSubscription.isFavorite = false;

                            return this.createResource(newSubscription, { actionType: ResourceActionTypes.COPY });
                        }),
                        tap((id) => this.monitoringRoutingService.gotoUpdateMediaSubscription(id)),
                        switchMap(
                            () => {
                                return this.refreshAllManual().pipe(
                                    filter(x => !!x),
                                    take(1),
                                );
                            }
                        )
                    );
                }
            }
        };
        this.coreDialogService.open(NewResourceDialogComponent, io);
    }

    /**
     * Removes the provided subscription ids from all display groups & refresh all$
     * @TODO Remove this function and ise updateGRoup
     */
    removeSubscriptionsFromGroup(ids: number[]): Observable<void> {
        return this.mediaSubscriptionApiService.removeDisplayGroup(ids).pipe(
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            })
        );
    }


    updateFavorites(addIds: number[], removeIds: number[]): Observable<void> {
        return this.mediaSubscriptionApiService.updateFavorites(addIds, removeIds).pipe(
            catchError((error) => {
                this.messageService.add({
                    severity: messageSeverityEnum.error,
                    summary: 'Common.lhp.moveToGroup.error.summary',
                    detail: 'Common.lhp.moveToGroup.error.detail'
                });

                throw new Error(error);
            }),
            switchMap(() => {
                return this.refreshAllManual().pipe(
                    filter(x => !!x),
                    take(1),
                    map(x => null)
                );
            }),
        );
    }


    messageCenter(actionResult: ResourceAction) {
        const actionType = actionResult.actionType;
        const error = actionResult.error;
        const resource = actionResult.resource as MediaSubscription;

        switch (actionType) {
            case ResourceActionTypes.CREATE: {
                if (error) {
                    // handle specific duplicate name error, since /socialsubscription endpoint throw errors
                    // if non-agent name matches agent names.
                    if ((error?.error?.detail?.[0] as CommonApiErrorDetailsModel)?.code === this.DUPLICATE_NAME_ERROR_CODE) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MediaSubscriptions.createSubscriptionErrorHeading',
                            detail: 'MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MediaSubscriptions.createSubscriptionErrorHeading',
                            detail: 'MediaSubscriptions.createSubscriptionErrorMessage'
                        });
                    }
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MediaSubscriptions.createSubscriptionSuccessHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.createSubscriptionSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.UPDATE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MediaSubscriptions.updateSubscriptionErrorHeading',
                        detail: 'MediaSubscriptions.updateSubscriptionErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MediaSubscriptions.updateSubscriptionSuccessHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.updateSubscriptionSuccessMessage', {
                            name: resource.name
                        })
                    });
                }

                break;
            }

            case ResourceActionTypes.DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitoring.mediaSubscriptions.deleteMediaSubscriptionErrorHeading',
                        detail: this.localizationService.transform('Monitoring.mediaSubscriptions.deleteMediaSubscriptionErrorMessage',
                            {  name: resource.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Monitoring.mediaSubscriptions.deleteMediaSubscriptionSuccessHeading',
                        detail: this.localizationService.transform('Monitoring.mediaSubscriptions.deleteMediaSubscriptionSuccessMessage',
                            { name: resource.name }
                        )
                    });
                }

                break;
            }

            case ResourceActionTypes.BULK_DELETE: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitoring.mediaSubscriptions.bulkDeleteMediaSubscriptionErrorHeading',
                        detail: 'Monitoring.mediaSubscriptions.bulkDeleteMediaSubscriptionErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Monitoring.mediaSubscriptions.bulkDeleteMediaSubscriptionSuccessHeading',
                        detail: this.localizationService.transform(
                            'Monitoring.mediaSubscriptions.bulkDeleteMediaSubscriptionSuccessMessage',
                            { number: actionResult.count }
                        )
                    });
                }

                break;
            }

            case ResourceActionTypes.ACTIVATED: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MediaSubscriptions.subscriptionActivatedErrorHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.subscriptionActivatedErrorMessage', {
                            name: resource.name
                        })
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MediaSubscriptions.subscriptionActivatedHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.subscriptionActivatedMessage',
                            { name: resource.name }
                        )
                    });
                }

                break;
            }

            case ResourceActionTypes.PAUSED: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'MediaSubscriptions.subscriptionPausedErrorHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.subscriptionPausedErrorMessage',
                            { name: resource.name }
                        )
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MediaSubscriptions.subscriptionPausedHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.subscriptionPausedMessage',
                            { name: resource.name }
                        )
                    });
                }

                break;
            }

            case ResourceActionTypes.RENAME: {
                if (error) {
                    // handle specific duplicate name error, since /socialsubscription endpoint throw errors
                    // if non-agent name matches agent names. UPDATE method return number instead of string code
                    if (+(error?.error?.detail?.[0] as CommonApiErrorDetailsModel)?.code === Number(this.DUPLICATE_NAME_ERROR_CODE)) {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MediaSubscriptions.renameSubscriptionErrorHeading',
                            detail: 'MediaSubscriptions.saveMediaSubscriptionsDuplicateNameErrorMessage'
                        });
                    } else {
                        this.messageService.add({
                            severity: messageSeverityEnum.error,
                            summary: 'MediaSubscriptions.renameSubscriptionErrorHeading',
                            detail: 'MediaSubscriptions.renameSubscriptionErrorMessage'
                        });
                    }
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'MediaSubscriptions.renameSubscriptionSuccessHeading',
                        detail: this.localizationService.transform('MediaSubscriptions.renameSubscriptionSuccessMessage',
                            { name: resource.name }
                        )
                    });
                }

                break;
            }

            case ResourceActionTypes.COPY: {
                if (error) {
                    this.messageService.add({
                        severity : messageSeverityEnum.error,
                        summary: 'Monitoring.copySubscription',
                        detail: 'Discover.confirmDialog.copy.error'
                    });
                } else {
                    this.messageService.add({
                        severity : messageSeverityEnum.success,
                        summary: 'Monitoring.copySubscription',
                        detail: 'Discover.confirmDialog.copy.success'
                    });
                }

                break;
            }

            case ResourceActionTypes.BULK_VISIBILITY: {
                if (error) {
                    this.messageService.add({
                        severity: messageSeverityEnum.error,
                        summary: 'Monitoring.mediaSubscriptions.bulkUpdateMediaSubscriptionErrorHeading',
                        detail: 'Monitoring.mediaSubscriptions.bulkUpdateMediaSubscriptionErrorMessage'
                    });
                } else {
                    this.messageService.add({
                        severity: messageSeverityEnum.success,
                        summary: 'Monitoring.mediaSubscriptions.bulkUpdateMediaSubscriptionSuccessHeading',
                        detail: this.localizationService.transform('Monitoring.mediaSubscriptions.bulkUpdateMediaSubscriptionSuccessMessage',
                            { number: actionResult.count }
                        )
                    });
                }

                break;
            }
        }
    }


    getFilterListItems(): Observable<AuditableModel[]> {
        return this.all$;
    }


    /**
     * Checks for unsaved changes, if approved, navigate to the new subscription route
     */
    navigateToNewMediaSubscription() {
        this.canDeactivate$().subscribe(
            response => {
                if (response) {
                    this.monitoringRoutingService.gotoNewMediaSubscription();
                }
            }
        );
    }

    /**
     * Responds to changes in the route by updating media subscriptions
     * Sets a blank subscription when the subscription route is hit with no id
     */
    private onUpdateRoute(route: MonitoringRouteModel): Observable<MediaSubscription> {
        this.currentRoute = route;
        const id: number = +route.value;
        const type: RouteType = route.type;

        if (type === RouteType.subscription) {
            if (!id) {
                // No ID means new subscription
                this.setCurrentResource(new MediaSubscription());
            } else {
                return this.all$.pipe(
                    take(1),
                    map(subscriptions => {
                        const currentSub = subscriptions.find(x => x.id === id);
                        if (!currentSub) {
                            this.monitoringRoutingService.gotoSubscriptionHype({
                                queryParams: { notFound: 'true' }
                            });
                            return null;
                        }
                        this.setCurrentResource(currentSub);

                        return currentSub;
                    })
                );
            }
        } else if (type === RouteType.subscriptionResults ||
            type === RouteType.subscriptionAnalyticsDashboard ||
            type === RouteType.subscriptionAnalyticsSyndicates) {
            return this.all$.pipe(
                take(1),
                map(subscriptions => {
                    const currentSub = subscriptions.find(x => x.id === id);
                    if (!currentSub) {
                        this.monitoringRoutingService.gotoSubscriptionHype({
                            queryParams: { notFound: 'true' }
                        });
                        return null;
                    }
                    this.setCurrentResource(currentSub);

                    return currentSub;
                }),
                filter(x => !!x),
                tap((currentResource) => {
                    const search: MentionSearchModel = currentResource.toMentionSearchModel();

                    if ((this.currentRoute.type === RouteType.topicAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.adHocAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.allSavedCoverageAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.folderAnalyticsSyndicates) ||
                        (this.currentRoute.type === RouteType.subscriptionAnalyticsSyndicates)
                    ) {
                        this.mentionSearchService.setShowSyndicates(false);
                    } else {
                        this.mentionSearchService.setShowSyndicates(true);
                    }
                    this.mentionSearchService.setSearchCriteria(search.searchCriteria);
                    if (!this.searchResultsService.filtersLocked()) {
                        const filtersQueryString = this.currentRoute.queryParamMap?.get('filters');
                        if (filtersQueryString) {
                            // use date query from filter of route params
                            const dateQuery = MentionSearchCriteriaModel.fromFilterQueryString(filtersQueryString)?.search.query.dateQuery;
                            if (dateQuery) {
                                this.mentionSearchService.setDateQuery(dateQuery);
                            }
                        } else if (!this.searchResultsService.getIsViewModeChange()) {
                            this.mentionSearchService.setDateQuery(DateQueryModel.getLast24HoursQuery());
                            this.mentionSearchService.clearFilters();
                        }
                    } else {
                        this.searchResultsService.loadLockedFiltersFromSession();
                    }
                    this.mentionSearchService.clearSearch();
                    this.mentionSearchService.search(true, true);
                })
            );
        } else if (type === RouteType.subscriptionsHype) {
            this.setCurrentResource(null);
        }

        return of(null);
    }


    /**
     * Gets the N top subscriptions based on Quota Usage
     * @param count Number of subscriptions to get
     */
    getTopSubscriptions(count: number): Observable<{subscription: MediaSubscription; count: number}[]> {

        return combineLatest([
            this.all$,
            this.mediaSubscriptionApiService.getTopSubscriptions(count, false)
        ]).pipe(
            take(1),
            map(([subscriptions, values]) => {
                return values
                    .map(value => {
                        return {
                            subscription: subscriptions.find((sub) => +sub.id === +value.value),
                            count: value.count
                        };
                    })
                    // Filter out ids that we couldn't find subscriptions for
                    .filter(combo => !!combo.subscription);
            })
        );
    }

    /**
     * Gets the quota usage over time data for the account
     */
    getUsageOverTimeSummary(): Observable<SubscriptionContentTimeslot[]> {
        return this.mediaSubscriptionApiService.getContentOverTime();
    }

    /**
     * Gets the quota usage over time data for a single subscription
     */
    getSubscriptionUsageOverTime(id: number): Observable<SubscriptionContentTimeslot[]> {
        return this.mediaSubscriptionApiService.getContentOverTime(id);
    }

    getIsQuotaExceeded(quotaType: MonitoringQuotaTypesEnum): Observable<boolean | null> {
        return quotaType === MonitoringQuotaTypesEnum.SOCIAL_LISTENING
            ? this.socialListeningQuotaExceeded$
            : this.mediaSubscriptionQuotaExceeded$;
    }

    getQuotaInfo(quotaType: MonitoringQuotaTypesEnum): Observable<QuotaInformation | null> {
        return quotaType === MonitoringQuotaTypesEnum.SOCIAL_LISTENING
            ? this.socialListeningQuotaInfo$
            : this.mediaSubscriptionQuotaInfo$;
    }

    getUsageHistory(timeStamp?: number, needHistory = true): Observable<[QuotaInformation[], QuotaHistoryInformation[]]> {
        return this.userStateService.hasAnyPermissions([userPermissionsEnum.mediaSubscriptions, userPermissionsEnum.socialIntelligence]).pipe(
            filter(hasPermission => !!hasPermission),
            switchMap(() => forkJoin([
                this.mediaSubscriptionApiService.getAccountQuotaInfo(timeStamp),
                needHistory ? this.mediaSubscriptionApiService.getAccountQuotaHistoryInfo(timeStamp) : of([])
            ]))
        );
    }
}
